import { Component, Fragment } from 'react'
import { BoxProps, Grid, RegularBreakpoints, SxProps, Theme } from '@mui/material'
import type { ComponentType } from 'react'
import { cleanObject } from '../utils'
import { mapGlobalModalContext } from '../api-context'
import { IFormBase, IFormBaseContext, IFormInputBase } from './types'
import { FormGridLayoutStyled, CreateFormBottomBar, formGridLayoutClasses } from './create.form-grid-layout.units'
import FormValidator, { IPartialError } from './validator'
import createFormBase, { IFormBaseSlots } from './create.form-base'
import createInput from './create.input'

export interface IFormGridLayoutConfig<T> {
  key: keyof T
  label?: string
  placeholder?: string
  sizes?: RegularBreakpoints
  defaultValue?: any
  className?: string
  inputElement?: ComponentType<IFormInputBase<T>>
}

export type IPreSubmitEvent<T> = (value: Partial<T>, oldValue?: T) => Partial<T>

export interface IFormGridLayoutMergedConfigs<T> {
  hiddenFields?: Partial<Record<keyof T, boolean>> | ((state: Partial<T>, model?: Partial<T>) => Partial<Record<keyof T, boolean>>)
  disabledFields?: Partial<Record<keyof T, boolean>> | ((state: Partial<T>, model?: Partial<T>) => Partial<Record<keyof T, boolean>>)
}

export interface IFormGridLayoutSlots<T> extends IFormGridLayoutMergedConfigs<T> {
  action?: ComponentType<IFormBase<T>>
  actionBefore?: JSX.Element
  contentBefore?: JSX.Element
  contentAfter?: JSX.Element
  // getDisabledFields?: (value: Partial<T>) => Partial<Record<keyof T, boolean>>
  closeState?: { Success?: boolean; Fail?: boolean }
  rootProps?: BoxProps
  preSubmit?: IPreSubmitEvent<T>
  formSlots?: IFormBaseSlots
}

export interface IFormGridLayoutParams<T> extends IFormGridLayoutSlots<T> {
  configs: IFormGridLayoutConfig<T>[]
  validate: FormValidator<Partial<T>>
  autoHeight?: boolean
}

export interface IFormGridLayoutProps<T> {
  data?: T
  onSubmit: (value: Partial<T>, signal?: AbortSignal) => Promise<void>
  onError?: (error: any) => void
  onSubmitError?: (error: IPartialError<T>) => void
  onClose?: () => void
  sx?: SxProps<Theme>
  slots?: IFormGridLayoutSlots<T>
  autoHeight?: boolean
}

export interface IFormGridLayoutState {
  loadding?: boolean
}

const CreateFormGridLayout = function <T>(params: IFormGridLayoutParams<T>): ComponentType<IFormGridLayoutProps<T>> {
  const FormBaseInstance = createFormBase<T>()
  const BottomBarInstance = CreateFormBottomBar<T>()
  const InputBaseInstance = createInput<T>({ maxLength: 250 })

  class FormGridLayout extends Component<IFormGridLayoutProps<T>, IFormGridLayoutState> {
    private abortController?: AbortController
    constructor(props: IFormGridLayoutProps<T>) {
      super(props)
      this.state = { loadding: false }
    }

    get configMerged() {
      const { slots = {} } = this.props
      return {
        hiddenFields: slots.hiddenFields ?? params.hiddenFields ?? (() => ({}) as Partial<Record<keyof T, boolean>>),
        disabledFields: slots.disabledFields ?? params.disabledFields ?? (() => ({}) as Partial<Record<keyof T, boolean>>)
      }
    }

    render() {
      const { slots, data, autoHeight } = this.props
      const BottomBar = params?.action ?? slots?.action ?? BottomBarInstance

      const rootClasses = [formGridLayoutClasses.root, slots?.rootProps?.className]
      if (autoHeight ?? params.autoHeight) rootClasses.push(formGridLayoutClasses.autoHeight)

      return mapGlobalModalContext(({ close }) => (
        <FormGridLayoutStyled {...slots?.rootProps} className={rootClasses.filter(Boolean).join(' ')}>
          <FormBaseInstance.Form
            className={formGridLayoutClasses.form}
            validate={params.validate}
            onSubmit={(v) => this.onSubmit(v, close)}
            slots={this.getFormSlots()}
            onError={this.props.onSubmitError}
          >
            {slots?.contentBefore && slots?.contentBefore}
            {params?.contentBefore}
            {FormBaseInstance.mapContext((context) => {
              const { hiddenFields } = this.configMerged
              const mState = context.modelState || {}
              const finalHiddenFields = typeof hiddenFields === 'function' ? hiddenFields(mState, data) : hiddenFields || {}
              return (
                <Grid container spacing={2}>
                  {params.configs.map((config, index) => {
                    const isHidden: boolean | undefined = finalHiddenFields[config.key] ? true : undefined
                    if (isHidden) return <Fragment key={config.key.toString() + index} />
                    return (
                      <Fragment key={config.key.toString() + index}>
                        <Grid item xs={12} {...config.sizes} className={config.className}>
                          {this.renderFormFieldElement(context, config)}
                        </Grid>
                      </Fragment>
                    )
                  })}
                </Grid>
              )
            })}
            {params?.contentAfter}
            {slots?.contentAfter && slots?.contentAfter}
            {FormBaseInstance.mapContext((context) => (
              <BottomBar data={data} onBlur={context.onBlur} messageErrors={context.messageErrors} before={slots?.actionBefore} />
            ))}
          </FormBaseInstance.Form>
        </FormGridLayoutStyled>
      ))
    }

    renderFormFieldElement = (context: IFormBaseContext<T>, config: IFormGridLayoutConfig<T>) => {
      const { data } = this.props
      const ElementComponent = config.inputElement ?? InputBaseInstance
      const mState = context.modelState || {}
      const { disabledFields } = this.configMerged
      const finalDisabledFields = typeof disabledFields === 'function' ? disabledFields(mState, data) : disabledFields || {}
      const obj: IFormInputBase<T, any> = {
        data,
        onBlur: context.onBlur,
        messageErrors: context.messageErrors,
        name: config.key,
        label: config.label,
        placeholder: config.placeholder,
        disabled: finalDisabledFields[config.key] ? true : undefined,
        defaultValue: data?.[config.key] ?? config?.defaultValue,
        formContext: context
      }
      return <ElementComponent {...cleanObject(obj)} />
    }

    loading = () => this.setState({ loadding: true })

    unloading = () => this.setState({ loadding: false })

    componentWillUnmount(): void {
      this.abortController?.abort()
    }

    onSubmit = async (value: Partial<T>, close?: () => void) => {
      const { slots } = this.props
      const mapping = this.props.slots?.preSubmit ?? params.preSubmit ?? this.preSubmit
      const data = mapping(value, this.props.data)
      try {
        this.loading()
        this.abortController?.abort()
        this.abortController = new AbortController()
        await this.props.onSubmit(data, this.abortController?.signal)
        if (!slots?.closeState || slots.closeState.Success !== false) {
          close && close()
          this.props.onClose && this.props.onClose()
        }
      } catch (error) {
        if (slots?.closeState && slots.closeState.Fail === true) {
          close && close()
          this.props.onClose && this.props.onClose()
        }
        if (this.props.onError) this.props.onError(error)
        throw error
      } finally {
        this.unloading()
      }
    }

    preSubmit: IPreSubmitEvent<T> = (value) => value

    getFormSlots = (): IFormBaseSlots => {
      const isLoading = this.state.loadding
      const { formProps } = this.props.slots?.formSlots || {}
      return {
        formProps: {
          ...formProps,
          sx: {
            ...this.props.sx,
            opacity: isLoading ? 0.7 : 1,
            pointerEvents: isLoading ? 'none' : 'auto'
          }
        }
      }
    }
  }
  return FormGridLayout
}

export default CreateFormGridLayout
